import { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Slider } from '../components/ui/slider';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { CalendarIcon, Users, Trash2, X } from 'lucide-react';
import { format, parseISO, eachWeekOfInterval, startOfWeek } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '../lib/utils';
import { generateId } from '../data/sampleData';
import { getWeekKey } from '../utils/dateUtils';

export const TaskDialog = ({ open, onOpenChange, task, projects, resources, onSave, onDelete, canEditTasks, isAdmin, currentUsername, defaultProjectId, mentionUsers = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    projectId: '',
    startDate: '',
    endDate: '',
    progress: 0,
    resourceAllocations: [],
    comments: []
  });
  const [commentText, setCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const initialTaskRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const commentInputRef = useRef(null);
  const [mentionState, setMentionState] = useState({ active: false, query: '', start: -1, end: -1 });
  const editCommentInputRef = useRef(null);
  const [editMentionState, setEditMentionState] = useState({ active: false, query: '', start: -1, end: -1 });

  const normalizeTaskData = function(data) {
    return {
      name: data?.name || '',
      projectId: data?.projectId || '',
      startDate: data?.startDate || '',
      endDate: data?.endDate || '',
      progress: typeof data?.progress === 'number' ? data.progress : 0,
      resourceAllocations: Array.isArray(data?.resourceAllocations) ? data.resourceAllocations : [],
      comments: Array.isArray(data?.comments) ? data.comments : []
    };
  };

  // Deduplicate projects and resources
  const uniqueProjects = useMemo(function() {
    const seen = {};
    const result = [];
    for (let i = 0; i < projects.length; i++) {
      const p = projects[i];
      if (!seen[p.id]) {
        seen[p.id] = true;
        result.push(p);
      }
    }
    return result;
  }, [projects]);

  const uniqueResources = useMemo(function() {
    const seen = {};
    const result = [];
    for (let i = 0; i < resources.length; i++) {
      const r = resources[i];
      if (!seen[r.id]) {
        seen[r.id] = true;
        result.push(r);
      }
    }
    return result;
  }, [resources]);

  useEffect(function() {
    const initialData = task ? {
      name: task.name,
      projectId: task.projectId,
      startDate: task.startDate,
      endDate: task.endDate,
      progress: task.progress,
      resourceAllocations: task.resourceAllocations || [],
      comments: task.comments || []
    } : {
      name: '',
      projectId: defaultProjectId || '',
      startDate: '',
      endDate: '',
      progress: 0,
      resourceAllocations: [],
      comments: []
    };
    if (task) {
      setFormData(initialData);
    } else {
      setFormData(initialData);
    }
    initialTaskRef.current = normalizeTaskData(initialData);
    setCommentText('');
    setEditingCommentId(null);
    setEditingCommentText('');
    setMentionState({ active: false, query: '', start: -1, end: -1 });
    setEditMentionState({ active: false, query: '', start: -1, end: -1 });
  }, [task, open, uniqueProjects, defaultProjectId]);

  // Calculate weeks between task dates
  const taskWeeks = useMemo(function() {
    if (!formData.startDate || !formData.endDate) return [];
    try {
      const start = startOfWeek(parseISO(formData.startDate), { weekStartsOn: 1 });
      const end = parseISO(formData.endDate);
      const weeks = eachWeekOfInterval({ start: start, end: end }, { weekStartsOn: 1 });
      const result = [];
      for (let i = 0; i < weeks.length; i++) {
        result.push({
          date: weeks[i],
          key: getWeekKey(weeks[i]),
          label: format(weeks[i], 'dd/MM', { locale: it })
        });
      }
      return result;
    } catch (e) {
      return [];
    }
  }, [formData.startDate, formData.endDate]);

  const handleResourceToggle = function(resourceId, checked) {
    if (checked) {
      const weeklyHours = {};
      for (let i = 0; i < taskWeeks.length; i++) {
        weeklyHours[taskWeeks[i].key] = 20;
      }
      setFormData(function(prev) {
        return {
          ...prev,
          resourceAllocations: [...prev.resourceAllocations, { resourceId: resourceId, weeklyHours: weeklyHours }]
        };
      });
    } else {
      setFormData(function(prev) {
        return {
          ...prev,
          resourceAllocations: prev.resourceAllocations.filter(function(ra) { return ra.resourceId !== resourceId; })
        };
      });
    }
  };

  const handleHoursChange = function(resourceId, weekKey, hours) {
    setFormData(function(prev) {
      return {
        ...prev,
        resourceAllocations: prev.resourceAllocations.map(function(ra) {
          if (ra.resourceId === resourceId) {
            const newWeeklyHours = { ...ra.weeklyHours };
            newWeeklyHours[weekKey] = parseInt(hours) || 0;
            return { ...ra, weeklyHours: newWeeklyHours };
          }
          return ra;
        })
      };
    });
  };

  const handleProjectChange = function(projectId) {
    setFormData(function(prev) {
      return { ...prev, projectId: projectId };
    });
  };

  const handleSubmit = async function(e) {
    e.preventDefault();
    if (!formData.name || !formData.projectId || !formData.startDate || !formData.endDate) return;
    if (!canEdit) return;
    if (savingRef.current) return;

    savingRef.current = true;
    setIsSaving(true);
    try {
      await onSave(buildTaskPayload());
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
    onOpenChange(false);
  };

  const handleDelete = function() {
    if (task && onDelete) {
      onDelete(task.id);
      onOpenChange(false);
    }
  };

  const getInitials = function(name) {
    if (!name) return '?';
    const parts = name.split(' ');
    let result = '';
    for (let i = 0; i < parts.length && i < 2; i++) {
      if (parts[i][0]) result += parts[i][0];
    }
    return result.toUpperCase();
  };

  const isEditing = task !== null;
  const selectedResourceIds = formData.resourceAllocations.map(function(ra) { return ra.resourceId; });
  const sortedComments = useMemo(function() {
    return (formData.comments || []).slice().sort(function(a, b) {
      const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [formData.comments]);
  const username = currentUsername || localStorage.getItem('gantt-username') || 'admin';
  const canEdit = !!canEditTasks;
  const isFormValid = !!formData.name && !!formData.projectId && !!formData.startDate && !!formData.endDate;
  const normalizedFormData = useMemo(function() {
    return normalizeTaskData(formData);
  }, [formData]);
  const isDirty = useMemo(function() {
    if (!isEditing) return false;
    if (!initialTaskRef.current) return false;
    return JSON.stringify(normalizedFormData) !== JSON.stringify(initialTaskRef.current);
  }, [isEditing, normalizedFormData]);

  const buildTaskPayload = function(overrideData) {
    const data = overrideData || formData;
    return {
      id: task?.id || generateId('t'),
      name: data.name,
      projectId: data.projectId,
      startDate: data.startDate,
      endDate: data.endDate,
      progress: data.progress,
      resourceAllocations: data.resourceAllocations,
      comments: data.comments
    };
  };

  const computeMentionState = function(text, cursor) {
    if (typeof text !== 'string') {
      return { active: false, query: '', start: -1, end: -1 };
    }
    const safeCursor = typeof cursor === 'number' ? cursor : text.length;
    const before = text.slice(0, safeCursor);
    const atIndex = before.lastIndexOf('@');
    if (atIndex < 0) {
      return { active: false, query: '', start: -1, end: -1 };
    }
    if (atIndex > 0 && /[^\s]/.test(before[atIndex - 1])) {
      return { active: false, query: '', start: -1, end: -1 };
    }
    const query = before.slice(atIndex + 1);
    if (!/^[A-Za-z0-9._-]*$/.test(query)) {
      return { active: false, query: '', start: -1, end: -1 };
    }
    return { active: true, query: query, start: atIndex, end: safeCursor };
  };

  const updateMentionStateFromText = function(nextText) {
    const cursor = commentInputRef.current ? commentInputRef.current.selectionStart : nextText.length;
    setMentionState(computeMentionState(nextText, cursor));
  };

  const updateEditMentionStateFromText = function(nextText) {
    const cursor = editCommentInputRef.current ? editCommentInputRef.current.selectionStart : nextText.length;
    setEditMentionState(computeMentionState(nextText, cursor));
  };

  const filteredMentionUsers = useMemo(function() {
    const query = (mentionState.query || '').toLowerCase();
    const list = Array.isArray(mentionUsers) ? mentionUsers : [];
    const filtered = list.filter(function(name) {
      if (!name) return false;
      if (!query) return true;
      return name.toLowerCase().includes(query);
    });
    return filtered.slice(0, 8);
  }, [mentionState.query, mentionUsers]);

  const filteredEditMentionUsers = useMemo(function() {
    const query = (editMentionState.query || '').toLowerCase();
    const list = Array.isArray(mentionUsers) ? mentionUsers : [];
    const filtered = list.filter(function(name) {
      if (!name) return false;
      if (!query) return true;
      return name.toLowerCase().includes(query);
    });
    return filtered.slice(0, 8);
  }, [editMentionState.query, mentionUsers]);

  const handleSelectMention = function(name) {
    if (!name) return;
    const start = mentionState.start;
    const end = mentionState.end;
    if (start < 0 || end < 0) return;
    const before = commentText.slice(0, start);
    const after = commentText.slice(end);
    const insert = '@' + name + ' ';
    const nextText = before + insert + after;
    setCommentText(nextText);
    setMentionState({ active: false, query: '', start: -1, end: -1 });
    setTimeout(function() {
      if (commentInputRef.current) {
        const pos = (before + insert).length;
        commentInputRef.current.focus();
        commentInputRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleSelectEditMention = function(name) {
    if (!name) return;
    const start = editMentionState.start;
    const end = editMentionState.end;
    if (start < 0 || end < 0) return;
    const before = editingCommentText.slice(0, start);
    const after = editingCommentText.slice(end);
    const insert = '@' + name + ' ';
    const nextText = before + insert + after;
    setEditingCommentText(nextText);
    setEditMentionState({ active: false, query: '', start: -1, end: -1 });
    setTimeout(function() {
      if (editCommentInputRef.current) {
        const pos = (before + insert).length;
        editCommentInputRef.current.focus();
        editCommentInputRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleCloseViaX = function() {
    if (isEditing && canEdit && isFormValid && isDirty) {
      onSave(buildTaskPayload());
    }
    onOpenChange(false);
  };

  const saveTaskImmediate = function(nextData) {
    if (!isEditing || !canEdit) return;
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    Promise.resolve(onSave(buildTaskPayload(nextData)))
      .finally(function() {
        savingRef.current = false;
        setIsSaving(false);
      });
  };

  // Find current project name for display
  const currentProject = uniqueProjects.find(function(p) { return p.id === formData.projectId; });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh]" hideClose>
        <button
          type="button"
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          onClick={handleCloseViaX}
          data-testid="task-close-x"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Modifica Task' : 'Nuovo Task'}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modifica i dettagli del task' : 'Inserisci i dettagli del nuovo task'}
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="max-h-[calc(90vh-180px)] pr-4">
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="taskName">Nome Task</Label>
              <Input
                id="taskName"
                value={formData.name}
                onChange={function(e) { setFormData(function(prev) { return { ...prev, name: e.target.value }; }); }}
                placeholder="Es: Design System"
                disabled={!canEdit}
                data-testid="task-name-input"
              />
            </div>

            {/* Project selector using native select to avoid ResizeObserver issues */}
            <div className="space-y-2">
              <Label>Progetto</Label>
              <div className="relative">
                <select
                  value={formData.projectId}
                  onChange={function(e) { handleProjectChange(e.target.value); }}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer appearance-none"
                  disabled={!canEdit}
                  data-testid="task-project-select"
                >
                  <option value="" disabled>
                    Seleziona progetto
                  </option>
                  {uniqueProjects.map(function(p) {
                    return (
                      <option key={'proj-opt-' + p.id} value={p.id}>
                        {p.name}
                      </option>
                    );
                  })}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              {currentProject && (
                <div className="flex items-center gap-2 mt-1">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: currentProject.color }}
                  />
                  <span className="text-xs text-muted-foreground">{currentProject.name}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Inizio</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.startDate && "text-muted-foreground"
                      )}
                      disabled={!canEdit}
                      data-testid="task-start-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.startDate ? format(parseISO(formData.startDate), 'dd/MM/yyyy') : 'Seleziona'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.startDate ? parseISO(formData.startDate) : undefined}
                      onSelect={function(date) { 
                        setFormData(function(prev) { 
                          return { ...prev, startDate: date ? format(date, 'yyyy-MM-dd') : '' }; 
                        }); 
                      }}
                      locale={it}
                      initialFocus
                      disabled={!canEdit}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Data Fine</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.endDate && "text-muted-foreground"
                      )}
                      disabled={!canEdit}
                      data-testid="task-end-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.endDate ? format(parseISO(formData.endDate), 'dd/MM/yyyy') : 'Seleziona'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.endDate ? parseISO(formData.endDate) : undefined}
                      onSelect={function(date) { 
                        setFormData(function(prev) { 
                          return { ...prev, endDate: date ? format(date, 'yyyy-MM-dd') : '' }; 
                        }); 
                      }}
                      locale={it}
                      initialFocus
                      disabled={!canEdit}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Avanzamento: {formData.progress}%</Label>
              <Slider
                value={[formData.progress]}
                onValueChange={function(val) { setFormData(function(prev) { return { ...prev, progress: val[0] }; }); }}
                max={100}
                step={5}
                className="mt-2"
                disabled={!canEdit}
                data-testid="task-progress-slider"
              />
            </div>

            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Risorse e Ore Settimanali
              </Label>
              
              <div className="space-y-3 border border-border rounded-lg p-3">
                {uniqueResources.map(function(resource, index) {
                  const isSelected = selectedResourceIds.indexOf(resource.id) !== -1;
                  const allocation = formData.resourceAllocations.find(function(ra) { return ra.resourceId === resource.id; });
                  
                  return (
                    <div key={'resource-item-' + resource.id + '-' + index} className="space-y-2">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id={'resource-' + resource.id}
                          checked={isSelected}
                          onCheckedChange={function(checked) { handleResourceToggle(resource.id, checked); }}
                          disabled={!canEdit}
                          data-testid={'resource-checkbox-' + resource.id}
                        />
                        <Avatar className="h-7 w-7">
                          <AvatarImage src={resource.avatar} alt={resource.name} />
                          <AvatarFallback className="text-xs">{getInitials(resource.name)}</AvatarFallback>
                        </Avatar>
                        <Label 
                          htmlFor={'resource-' + resource.id}
                          className="text-sm cursor-pointer"
                        >
                          {resource.name}
                        </Label>
                      </div>
                      
                      {isSelected && taskWeeks.length > 0 && (
                        <div className="ml-10 grid grid-cols-4 gap-2">
                          {taskWeeks.map(function(week, wIndex) {
                            return (
                              <div key={'week-' + resource.id + '-' + week.key + '-' + wIndex} className="space-y-1">
                                <Label className="text-xs text-muted-foreground">{week.label}</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="40"
                                  value={allocation?.weeklyHours[week.key] || 0}
                                  onChange={function(e) { handleHoursChange(resource.id, week.key, e.target.value); }}
                                  className="h-8 text-sm"
                                  disabled={!canEdit}
                                  data-testid={'hours-' + resource.id + '-' + week.key}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3">
              <Label>Commenti</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Textarea
                    ref={commentInputRef}
                    value={commentText}
                    onChange={function(e) {
                      const nextText = e.target.value;
                      setCommentText(nextText);
                      updateMentionStateFromText(nextText);
                    }}
                    onClick={function(e) { updateMentionStateFromText(e.target.value); }}
                    onKeyUp={function(e) { updateMentionStateFromText(e.target.value); }}
                    placeholder="Aggiungi un commento..."
                    rows={3}
                    disabled={!canEdit}
                    data-testid="task-comment-input"
                  />
                  {mentionState.active && filteredMentionUsers.length > 0 && (
                    <div className="absolute z-50 mt-1 w-64 rounded-md border border-border bg-background shadow-md">
                      {filteredMentionUsers.map(function(name) {
                        return (
                          <button
                            key={'mention-' + name}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60"
                            onMouseDown={function(e) {
                              e.preventDefault();
                              handleSelectMention(name);
                            }}
                          >
                            @{name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={function() {
                    const text = commentText.trim();
                    if (!text) return;
                    const newComment = {
                      id: generateId('c'),
                      text: text,
                      createdAt: new Date().toISOString(),
                      author: username
                    };
                    setFormData(function(prev) {
                      const next = {
                        ...prev,
                        comments: [newComment].concat(prev.comments || [])
                      };
                      saveTaskImmediate(next);
                      return next;
                    });
                    setCommentText('');
                    setMentionState({ active: false, query: '', start: -1, end: -1 });
                  }}
                  disabled={!commentText.trim() || !canEdit}
                  className="sm:self-start"
                  data-testid="task-comment-add"
                >
                  Aggiungi
                </Button>
              </div>
              <div className="space-y-2 border border-border rounded-lg p-3 max-h-48 overflow-auto">
                {sortedComments.length === 0 && (
                  <div className="text-sm text-muted-foreground">
                    Nessun commento ancora.
                  </div>
                )}
                {sortedComments.map(function(comment) {
                  const dateLabel = comment.createdAt
                    ? format(parseISO(comment.createdAt), 'dd/MM/yyyy HH:mm')
                    : 'Data non disponibile';
                  const canManageComment = canEdit && (isAdmin || (comment.author && comment.author === username));
                  const isEditingComment = editingCommentId === comment.id;
                  return (
                    <div key={comment.id} className="border-b border-border/50 pb-2 last:border-b-0 last:pb-0">
                      <div className="text-xs text-muted-foreground">
                        {dateLabel}{comment.author ? ' • ' + comment.author : ''}
                      </div>
                      {isEditingComment ? (
                        <div className="space-y-2">
                          <div className="relative">
                            <Textarea
                              ref={editCommentInputRef}
                              value={editingCommentText}
                              onChange={function(e) {
                                const nextText = e.target.value;
                                setEditingCommentText(nextText);
                                updateEditMentionStateFromText(nextText);
                              }}
                              onClick={function(e) { updateEditMentionStateFromText(e.target.value); }}
                              onKeyUp={function(e) { updateEditMentionStateFromText(e.target.value); }}
                              rows={3}
                              data-testid={'task-comment-edit-' + comment.id}
                            />
                            {editMentionState.active && filteredEditMentionUsers.length > 0 && (
                              <div className="absolute z-50 mt-1 w-64 rounded-md border border-border bg-background shadow-md">
                                {filteredEditMentionUsers.map(function(name) {
                                  return (
                                    <button
                                      key={'mention-edit-' + name}
                                      type="button"
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60"
                                      onMouseDown={function(e) {
                                        e.preventDefault();
                                        handleSelectEditMention(name);
                                      }}
                                    >
                                      @{name}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              onClick={function() {
                                const nextText = editingCommentText.trim();
                                if (!nextText) return;
                                setFormData(function(prev) {
                                  const next = {
                                    ...prev,
                                    comments: (prev.comments || []).map(function(c) {
                                      if (c.id === comment.id) {
                                        return { ...c, text: nextText };
                                      }
                                      return c;
                                    })
                                  };
                                  saveTaskImmediate(next);
                                  return next;
                                });
                                setEditingCommentId(null);
                                setEditingCommentText('');
                                setEditMentionState({ active: false, query: '', start: -1, end: -1 });
                              }}
                              disabled={!editingCommentText.trim()}
                              data-testid={'task-comment-save-' + comment.id}
                            >
                              Salva
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={function() {
                                setEditingCommentId(null);
                                setEditingCommentText('');
                                setEditMentionState({ active: false, query: '', start: -1, end: -1 });
                              }}
                              data-testid={'task-comment-cancel-' + comment.id}
                            >
                              Annulla
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm whitespace-pre-wrap">{comment.text}</div>
                      )}
                      {canManageComment && !isEditingComment && (
                        <div className="flex gap-2 mt-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={function() {
                              setEditingCommentId(comment.id);
                              setEditingCommentText(comment.text || '');
                              const baseText = comment.text || '';
                              setEditMentionState(computeMentionState(baseText, baseText.length));
                            }}
                            data-testid={'task-comment-edit-btn-' + comment.id}
                          >
                            Modifica
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={function() {
                              setFormData(function(prev) {
                                return {
                                  ...prev,
                                  comments: (prev.comments || []).filter(function(c) { return c.id !== comment.id; })
                                };
                              });
                            }}
                            data-testid={'task-comment-delete-btn-' + comment.id}
                          >
                            Elimina
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <DialogFooter className="mt-6 pt-4 flex-col sm:flex-row gap-2">
              {isEditing && onDelete && canEdit && (
                <Button 
                  type="button" 
                  variant="destructive" 
                  onClick={handleDelete}
                  className="sm:mr-auto"
                  data-testid="delete-task-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </Button>
              )}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={function() { onOpenChange(false); }}>
                  {canEdit ? 'Annulla' : 'Chiudi'}
                </Button>
                {canEdit && (
                  <Button 
                    type="submit" 
                    disabled={!isFormValid || isSaving}
                    data-testid="save-task-btn"
                  >
                    {isEditing ? 'Salva Modifiche' : 'Crea Task'}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDialog;
